import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetCashToSupplierCalculation } from 'services/calculations/cashFlow';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { PERIODS_OPERATION_TYPES, subtractPeriodValueBaseOnPreviousPeriod, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { createCashToSuppliersParams } from './helper';
import { IFormContentTypes, observedFields } from '../../utils/form';

export const useCashToSuppliers = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getCashToSupplierCalculation = useGetCashToSupplierCalculation();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [
    cashPaidToSuppliersFiled,
    inventoryField,
    accountsPayableField,
    deprecationAmortizationField,
    changeInInventoryField,
    changeInAccountsPayableField,
    deprecationField,
    amortizationField
  ] = observedFields.cashFlowToSuppliers.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (content) => {
        const { cashToSuppliersParams, unit } = createCashToSuppliersParams(content);

        const params = {
          unit,
          businessPlanId,
          cashToSuppliersParams,
        };

        try {
          const { cashPaidToSupplier, inventory, accountsPayable, amortization, depreciationFixedExpense } =
            await getCashToSupplierCalculation.mutateAsync(params);

          const changeInInventory = subtractPeriodValueBaseOnPreviousPeriod(
            inventory,
            String(cashToSuppliersParams.inventory),
          );
          const changeInAccountsPayable = subtractPeriodValueBaseOnPreviousPeriod(accountsPayable, String(cashToSuppliersParams.initAccountPayable));

          const deprecationAmortizationFixedExpense = summarizeSubtractPeriodValues([amortization, depreciationFixedExpense], PERIODS_OPERATION_TYPES.ADD);

          setValue(cashPaidToSuppliersFiled, cashPaidToSupplier);
          setValue(inventoryField, inventory);
          setValue(accountsPayableField, accountsPayable);
          setValue(deprecationAmortizationField, deprecationAmortizationFixedExpense);
          setValue(changeInInventoryField, changeInInventory);
          setValue(changeInAccountsPayableField, changeInAccountsPayable);
          setValue(deprecationField, depreciationFixedExpense);
          setValue(amortizationField, amortization);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 800),

    [],
  );

  const cashToSuppliersCalculationHandler = useCallback(
    async (content: IFormContentTypes['cashFlowToSuppliers']) => {
      if (!canAccess) return;

      debouncedUpdateFunction(content);
    },

    [debouncedUpdateFunction],
  );

  return cashToSuppliersCalculationHandler;
};
